import { types, flow, getEnv, getParent } from 'mobx-state-tree';
import { toJS } from 'mobx';
import { uniqBy, cloneDeep } from 'lodash-es';
import _orderBy from 'lodash-es/orderBy';
import { clean, getUmbrellaAutoDetails } from '@ourbranch/policy-utils';
import { policyTypes } from '@ourbranch/policy-types';

import Notification, { isPolicyCrossSellEligible } from 'core/helpers/notifications';
import { checkAdditionalCoverages } from 'core/helpers/quoter.service';
import { getMerged, getMergedAllSegments } from 'customer/components/policy/merge-segments';
import { getPolicyInForceOn } from 'customer/components/policy/policy-in-force';

import {
  GET_DOCUMENTS_AND_APPLICATIONS,
  GET_UPLOAD_URL,
  DELETE_INTERNAL_DOCUMENT,
  SET_DOCUMENT_LABEL,
  SET_DOCUMENT_POLICY,
  REGENERATE_POLICY_DOCUMENTS,
  SEND_POLICY_DOCUMENT
} from 'customer/components/documents/documents.queries';
import { SEND_APPLICATION_LINK } from 'customer/components/onboarding-task-center/components/application-url-form/application-links.queries';
import { DOWNLOAD_ANON_POLICY_DOC, GENERATE_TEMPORARY_DOCUMENT } from './policies.queries';
import { PolicyStore } from './policy-store';
import { getPolicyStatus, PolicyStatus } from 'core/helpers/policy-status';
import PolicyList from './models/policy-list';

export const PoliciesStore = types
  .model({
    loading: types.optional(types.boolean, false),
    list: types.array(PolicyList),
    loadingDocuments: types.optional(types.boolean, false),
    documents: types.maybeNull(types.frozen()),
    unsignedApplications: types.maybeNull(types.frozen()),
    unsignedBixConversions: types.maybeNull(types.frozen()),
    policy: types.maybe(PolicyStore),
    notifications: types.maybeNull(types.array(types.string))
  })
  .actions((self) => ({
    getDocuments: flow(function* getDocuments(accountId, isAgencyUser) {
      const { client } = getEnv(self);
      self.loadingDocuments = true;
      try {
        const { data } = yield client.query({
          query: GET_DOCUMENTS_AND_APPLICATIONS,
          variables: { accountId }
        });
        self.documents = data.documents;
        self.unsignedApplications = data.unsignedApplications;
        self.setNotifications(isAgencyUser);
        self.loadingDocuments = false;
      } catch {
        self.loadingDocuments = false;
        return [];
      }
    }),
    regeneratePolicyDocuments: flow(function* regeneratePolicyDocuments(policyId) {
      const { client } = getEnv(self);
      return yield client.mutate({
        mutation: REGENERATE_POLICY_DOCUMENTS,
        variables: {
          policyId
        }
      });
    }),
    generateTemporaryDocument: flow(
      function* generateTemporaryDocument(policyId, previewId, docType, email, addedChangeKey) {
        const { client } = getEnv(self);
        return yield client.query({
          query: GENERATE_TEMPORARY_DOCUMENT,
          variables: {
            policyId,
            previewId,
            docType,
            email,
            addedChangeKey
          }
        });
      }
    ),
    getUploadUrl: flow(function* getUploadUrl(accountId, fileName, mimeType) {
      const { client } = getEnv(self);
      return yield client.query({
        query: GET_UPLOAD_URL,
        variables: {
          accountId,
          fileName,
          mimeType
        }
      });
    }),
    deleteInternalDocument: flow(function* deleteInternalDocument(accountId, key) {
      const { client } = getEnv(self);
      return yield client.mutate({
        mutation: DELETE_INTERNAL_DOCUMENT,
        variables: {
          accountId,
          key
        }
      });
    }),
    setDocumentLabel: flow(function* setDocumentLabel(accountId, key, label) {
      const { client } = getEnv(self);
      return yield client.mutate({
        mutation: SET_DOCUMENT_LABEL,
        variables: {
          accountId,
          key,
          label
        }
      });
    }),
    setDocumentPolicy: flow(function* setDocumentPolicy(accountId, key, policyId) {
      const { client } = getEnv(self);
      return yield client.mutate({
        mutation: SET_DOCUMENT_POLICY,
        variables: {
          accountId,
          key,
          policyId
        }
      });
    }),
    sendApplicationLink: flow(function* sendApplicationUrl(accountId, policyId) {
      const { client } = getEnv(self);
      return yield client.mutate({
        mutation: SEND_APPLICATION_LINK,
        variables: {
          accountId,
          policyId
        }
      });
    }),
    sendPolicyDocument: flow(function* sendPolicyDocument({ policyId, docPath, sendEmail, sendLetter }) {
      const { client } = getEnv(self);
      const res = yield client.mutate({
        mutation: SEND_POLICY_DOCUMENT,
        fetchPolicy: 'no-cache',
        variables: { policyId, docPath, sendEmail, sendLetter }
      });
      return res?.data?.result || {};
    }),
    downloadAnonPolicyDocument: flow(function* downloadAnonPolicyDocument(policyId, asOfDate) {
      const { client } = getEnv(self);
      return yield client.mutate({
        mutation: DOWNLOAD_ANON_POLICY_DOC,
        variables: {
          policyId,
          asOfDate
        }
      });
    }),
    setNotifications: function setNotifications(isAgencyUser) {
      const notifications = [];
      const policy = self?.policy?.policy;
      const bixConversionDocsInfo = self.getUnsignedBixConversion(policy?.id);

      const activePolicies = self.list?.filter((p) => getPolicyStatus(p) === PolicyStatus.Active) || [];
      const isPolicyAgencySold = policy?.isAgentSold || policy?.offer?.quote?.isAgentSold;

      const isCrossSellEligible =
        activePolicies.length === 1 && policy && isPolicyCrossSellEligible(policy, isPolicyAgencySold, isAgencyUser);

      const accountStore = getParent(self);
      const parentStore = getParent(accountStore);
      const affinityOnPolicy = self.list[0]?.segments?.[0]?.global?.affinity;
      const affinityData =
        affinityOnPolicy && parentStore.affinityLookups.data.find(({ affinity }) => affinity === affinityOnPolicy);
      const serviceAgreement = affinityData?.data?.serviceAgreement;

      let isPendingThirdPartyPurchases = false;
      if (policy) {
        const { policyType, policyDetails, offer } = policy;
        const hasHome = policyType === policyTypes.Home || policyType === policyTypes.HABundle;

        if (hasHome && policyDetails) {
          const { homeCoverage, includeEarthquake, earthquakeCoverage, includeFlood, floodCoverage } = policyDetails;
          const { canAddEarthquake, canAddFlood } = checkAdditionalCoverages(offer, policyType, isAgencyUser);
          if (
            (!isAgencyUser && homeCoverage?.interestedInSeparateWindHail && !homeCoverage?.externalPolicyId) ||
            (canAddEarthquake && includeEarthquake && !earthquakeCoverage?.policyId) ||
            (canAddFlood && includeFlood && !floodCoverage?.policyId)
          ) {
            isPendingThirdPartyPurchases = true;
          }
        }
      }

      if (!bixConversionDocsInfo?.signedDocUploadedInS3) {
        notifications.push(Notification.Policy.SignedConversionDocuments);
      }
      if (isCrossSellEligible) {
        notifications.push(Notification.Policy.CrossSellEligibility);
      }
      if (isPendingThirdPartyPurchases) {
        notifications.push(Notification.Policy.PendingThirdPartyPurchases);
      }

      if (accountStore.incompleteTasks.length) {
        notifications.push(Notification.Policy.IncompleteTasks);
      }

      if (!isAgencyUser && isPolicyAgencySold && !serviceAgreement && affinityData) {
        notifications.push(Notification.Policy.TransferToAgency);
      }

      if (!isAgencyUser && isPolicyAgencySold && serviceAgreement && affinityData) {
        notifications.push(Notification.Customer.AgencyServicedByBranch);
      }

      if (notifications.length) {
        self.notifications = notifications;
      } else {
        self.notifications = null;
      }
    }
  }))
  .views((self) => ({
    // `targetDate` (a segment startDate, 'YYYY-MM-DD') selects the auto POLICY whose term is in force
    // on that date; the returned details always merge ALL of that policy's segments. Umbrella validation
    // on a Home renewal passes the renewal segment's startDate so it compares against the auto renewal
    // term (a separate policy), while mid-term auto endorsements (e.g. a BI limit increase effective
    // after the home segment started) stay visible instead of being filtered out by segment date.
    //
    // `targetEndDate` (the home policy's endDate) additionally validates every ALREADY-CREATED
    // active/future auto term overlapping [targetDate, targetEndDate): per UW, umbrella must hold on
    // each underlying term, so `autoCoverage` in the returned details is the weakest across them
    // (e.g. a created-but-not-yet-endorsed auto renewal term at 100/300 fails the gate). Terms that
    // don't exist yet can't be validated and simply aren't in the set.
    getAutoPolicyDetails(targetDate, targetEndDate) {
      const inactivePolicies = self.list.filter(
        (policy) =>
          policy.policyType === policyTypes.Auto &&
          (getPolicyStatus(policy) === PolicyStatus.Inactive ||
            (getPolicyStatus(policy) === PolicyStatus.Cancelled && !policy.endDate === policy.effectiveDate))
      );

      // How we prioritize auto policies for this logic:
      // 1. Active or Future
      // 2. In Cancellation
      // 3. Most Recent Inactive

      const activeOrFuturePolicies = self.list.filter(
        (policy) =>
          policy.policyType === policyTypes.Auto &&
          (getPolicyStatus(policy) === PolicyStatus.Active || getPolicyStatus(policy) === PolicyStatus.Future)
      );

      const policiesInCancellation = self.list.filter(
        (policy) => policy.policyType === policyTypes.Auto && getPolicyStatus(policy) === PolicyStatus.InCancellation
      );

      // this allows for either the most recent Inactive or Cancelled Policy to be check specifically for use in Umbrella validations
      const mostRecentInactive = self.getRecentNonActivePolicy(inactivePolicies);

      // When validating a specific (e.g. future-effective) home segment, prefer the auto policy whose term
      // covers that date. A future home renewal must validate against the future auto term, which can be a
      // SEPARATE policy (e.g. AUTO-003), not just a later segment of the current auto policy — so we select
      // across policies by date before merging segments. Omitting the date keeps the prior first-policy
      // behavior for callers (e.g. MPD/renters) that only need the current snapshot.
      const autoPolicyForDate = targetDate ? getPolicyInForceOn(activeOrFuturePolicies, targetDate) : undefined;
      const autoPolicy =
        autoPolicyForDate || activeOrFuturePolicies?.[0] || policiesInCancellation?.[0] || mostRecentInactive;

      // Merging every segment includes scheduled-but-not-yet-effective endorsements (staff can date
      // them up to 60 days out), so e.g. a scheduled BI decrease counts against umbrella eligibility
      // immediately — deliberately conservative.
      const mergeAll = (policyToMerge) => {
        const mappedSegments = policyToMerge.segments.slice().map((s) => clean(cloneDeep(toJS(s))));
        return mappedSegments.length >= 1 ? getMergedAllSegments(mappedSegments) : undefined;
      };

      let mergedPolicyDetails;
      if (autoPolicy) {
        mergedPolicyDetails = mergeAll(autoPolicy);
      }

      if (targetDate && targetEndDate) {
        // Umbrella eligibility matches the server: only active/future terms qualify.
        // Keep the cancellation/inactive fallback above for callers without a full date window.
        return getUmbrellaAutoDetails(
          activeOrFuturePolicies.map((p) => clean(cloneDeep(toJS(p)))),
          targetDate,
          targetEndDate
        );
      }
      return mergedPolicyDetails;
    },
    getHomePolicyDetails() {
      const homePolicy = self.list
        ? self.list.find(
            (policy) =>
              policy.policyType === policyTypes.Home &&
              (getPolicyStatus(policy) === PolicyStatus.Active || getPolicyStatus(policy) === PolicyStatus.Future)
          )
        : undefined;
      let mergedPolicyDetails;
      if (homePolicy) {
        const { segments } = homePolicy;
        const mappedSegments = segments.slice().map((segment) => clean(cloneDeep(segment)));
        if (mappedSegments.length >= 1) {
          mergedPolicyDetails = getMerged(
            mappedSegments,
            mappedSegments[segments.length - 1].segmentId,
            homePolicy.policyType
          );
        }
      }
      return mergedPolicyDetails;
    },
    get hasActiveOrFutureAutoPolicy() {
      return self.list
        ? self.list.find((policy) => {
            return (
              policy.policyType === policyTypes.Auto &&
              (getPolicyStatus(policy) === PolicyStatus.Active || getPolicyStatus(policy) === PolicyStatus.Future)
            );
          })
        : undefined;
    },

    hasOtherActiveOrFutureAutoPolicy(excludedPolicyId) {
      return self.list
        ? self.list.find((policy) => {
            return (
              policy.id !== excludedPolicyId &&
              policy.policyType === policyTypes.Auto &&
              (getPolicyStatus(policy) === PolicyStatus.Active || getPolicyStatus(policy) === PolicyStatus.Future)
            );
          })
        : undefined;
    },

    hasActiveOrFutureHomePolicy() {
      return self.list
        ? self.list.find((policy) => {
            return (
              policy.policyType === policyTypes.Home &&
              (getPolicyStatus(policy) === PolicyStatus.Active || getPolicyStatus(policy) === PolicyStatus.Future)
            );
          })
        : undefined;
    },

    hasSecondHome() {
      const activeAndFutureHomePolicies = self.list
        ? self.list.filter(
            (policy) =>
              policy.policyType === policyTypes.Home &&
              (getPolicyStatus(policy) === PolicyStatus.Active || getPolicyStatus(policy) === PolicyStatus.Future)
          )
        : [];

      // Need to filter by the stem of the policy to get unique ones becaue it can be possible that the policy
      // is Active and the renewal policy for the same home is already created with a status of Future
      const uniqueActiveAndFutureHomePolicies = uniqBy(activeAndFutureHomePolicies, (policy) => {
        const policyIdArray = policy.id.split('-');
        const stem = `${policyIdArray[0]}-${policyIdArray[1]}`;
        return stem;
      });

      return uniqueActiveAndFutureHomePolicies.length > 1;
    },
    getUnsignedBixConversion(policyId) {
      return {
        signedDocUploadedInS3: !self.unsignedBixConversions?.find((policy) => policy.policyId === policyId),
        signedTimestamp: self.policy.bixConversionSignedTimestamp
      };
    },
    getRecentPolicy() {
      //gets the most recent updated policy regardless of status
      const recent = _orderBy(
        self.list,
        (policy) => {
          const mostRecentVersion = policy.versionHistory.length - 1;
          return policy.versionHistory[mostRecentVersion].updatedDateTime;
        },
        ['desc']
      )[0];
      return recent;
    },
    getMostRecentActiveOrFuturePolicy() {
      //gets the most recent updated active or future policy
      // First, try to find active or future policies
      const activeOrFuturePolicies = self.list.filter((policy) => {
        const status = getPolicyStatus(policy);
        return status === PolicyStatus.Active || status === PolicyStatus.Future;
      });

      // If we have active/future policies, return the most recently updated one
      if (activeOrFuturePolicies.length > 0) {
        return _orderBy(
          activeOrFuturePolicies,
          (policy) => {
            const mostRecentVersion = policy.versionHistory.length - 1;
            return policy.versionHistory[mostRecentVersion].updatedDateTime;
          },
          ['desc']
        )[0];
      }

      // Fall back to the current getRecentPolicy behavior if no active/future policies
      return this.getRecentPolicy();
    },
    getRecentNonActivePolicy(policies) {
      //gets the most recent updated policy that is not active, needed for umbrella validations
      const recent = _orderBy(
        policies,
        (policy) => {
          const mostRecentVersion = policy.versionHistory.length - 1;
          return policy.versionHistory[mostRecentVersion].updatedDateTime;
        },
        ['desc']
      )[0];
      return recent;
    },
    get unsigned() {
      if (self.unsignedApplications && self.unsignedApplications.length && self.list.length) {
        // Filter unsigned applications to only include policies that are NOT cancelled
        const validApplications = self.unsignedApplications.filter((application) => {
          const policy = self.list.find((p) => p.id === application.policyId);
          if (!policy) {
            return false;
          }
          const status = getPolicyStatus(policy);
          return status !== PolicyStatus.Cancelled;
        });

        // Return the first valid application, or null if none exist
        return validApplications.length > 0 ? validApplications[0] : null;
      }
      return null;
    },
    get hasUmbrella() {
      return self.list.some(
        (policy) =>
          (policy.policyType === 'H' || policy.policyType === 'C') &&
          [PolicyStatus.Future, PolicyStatus.Active].includes(getPolicyStatus(policy)) &&
          policy.policyDetails.includeUmbrella
      );
    },
    // per UW, umbrella requires both home and auto with Branch; when the auto is in cancellation
    // or cancelled with no replacement term, the umbrella falls off at the home policy's renewal
    get umbrellaWillCancelAtRenewal() {
      return (
        self.hasUmbrella &&
        !self.hasActiveOrFutureAutoPolicy &&
        self.list.some(
          (policy) =>
            policy.policyType === policyTypes.Auto &&
            [PolicyStatus.InCancellation, PolicyStatus.Cancelled].includes(getPolicyStatus(policy))
        )
      );
    }
  }));
